import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText, Syringe, Stethoscope, FlaskConical, BedDouble, Ambulance,
  Siren, Droplets, Shield, Receipt, Award, Activity, Building2,
  Scissors, HeartPulse, ClipboardCheck, Eye,
} from 'lucide-react';
import NHCIdBadge from '../components/NHCIdBadge';
import { patientApi } from '../api';

interface TemplateInfo {
  type: string;
  label: string;
  description: string;
  icon: typeof FileText;
  color: string;
  bg: string;
  category: 'certificate' | 'document' | 'financial';
}

/**
 * Who issues each document, and where the patient's own copies live.
 * Nothing on this page is generated by the app: a certificate is issued by a
 * provider and arrives signed. The page used to offer "Generate & Preview" on
 * every card, which called a route that does not exist — so every button
 * failed, and had it worked it would have minted a fake certificate.
 */
const ISSUED_BY: Record<string, { by: string; whereLabel: string; where: string }> = {
  VACCINATION_CERTIFICATE: { by: 'the hospital or clinic that vaccinated you', whereLabel: 'My vaccinations', where: '/patient/vaccinations' },
  MEDICAL_FITNESS_CERTIFICATE: { by: 'your doctor after an examination', whereLabel: 'My certificates', where: '/patient/certificates' },
  SICK_LEAVE_CERTIFICATE: { by: 'the doctor who treated you', whereLabel: 'My certificates', where: '/patient/certificates' },
  LAB_REPORT_CERTIFICATE: { by: 'the laboratory that ran the test', whereLabel: 'My lab reports', where: '/patient/lab-reports' },
  PRESCRIPTION_CERTIFICATE: { by: 'the doctor who prescribed', whereLabel: 'My prescriptions', where: '/patient/prescriptions' },
  DISCHARGE_SUMMARY_CERTIFICATE: { by: 'the hospital that discharged you', whereLabel: 'Health Vault', where: '/patient/health-vault' },
  AMBULANCE_TRANSPORT_CERTIFICATE: { by: 'the ambulance service that moved you', whereLabel: 'Health Vault', where: '/patient/health-vault' },
  EMERGENCY_EVENT_CERTIFICATE: { by: 'the hospital that handled the emergency', whereLabel: 'Emergency records', where: '/patient/emergency-records' },
  BLOOD_DONATION_CERTIFICATE: { by: 'the blood bank that collected the donation', whereLabel: 'Blood bank', where: '/patient/blood-bank' },
  INSURANCE_CLAIM_CERTIFICATE: { by: 'your insurer, from the claim you filed', whereLabel: 'My claims', where: '/patient/claims' },
  HOSPITAL_ADMISSION_CERTIFICATE: { by: 'the admitting hospital', whereLabel: 'Health Vault', where: '/patient/health-vault' },
  SURGERY_PROCEDURE_CERTIFICATE: { by: 'the hospital or surgeon', whereLabel: 'Health Vault', where: '/patient/health-vault' },
  HEALTH_CHECKUP_CERTIFICATE: { by: 'the centre that ran the checkup', whereLabel: 'My certificates', where: '/patient/certificates' },
  INSTITUTION_CLEARANCE_CERTIFICATE: { by: 'the examining doctor', whereLabel: 'My certificates', where: '/patient/certificates' },
  INVOICE: { by: 'the pharmacy, lab or hospital that billed you', whereLabel: 'Invoices & payments', where: '/patient/lab-invoices' },
};

const TEMPLATES: TemplateInfo[] = [
  { type: 'VACCINATION_CERTIFICATE', label: 'Vaccination Certificate', description: 'Immunization records with dose details, batch numbers, and post-vaccination instructions', icon: Syringe, color: 'text-emerald-600', bg: 'bg-emerald-50', category: 'certificate' },
  { type: 'MEDICAL_FITNESS_CERTIFICATE', label: 'Medical Fitness Certificate', description: 'Certifies patient fitness for work, school, or specific activities', icon: HeartPulse, color: 'text-green-600', bg: 'bg-green-50', category: 'certificate' },
  { type: 'SICK_LEAVE_CERTIFICATE', label: 'Sick Leave Certificate', description: 'Official medical leave certificate with diagnosis and rest period', icon: BedDouble, color: 'text-blue-600', bg: 'bg-blue-50', category: 'certificate' },
  { type: 'LAB_REPORT_CERTIFICATE', label: 'Lab Report Verification', description: 'Authenticated lab report with NeoVerify seal for result verification', icon: FlaskConical, color: 'text-purple-600', bg: 'bg-purple-50', category: 'certificate' },
  { type: 'PRESCRIPTION_CERTIFICATE', label: 'Prescription Certificate', description: 'Official prescription with Rx details, dosage, and follow-up instructions', icon: Stethoscope, color: 'text-teal-600', bg: 'bg-teal-50', category: 'document' },
  { type: 'DISCHARGE_SUMMARY_CERTIFICATE', label: 'Discharge Summary', description: 'Complete hospital discharge record with treatment summary and follow-up plan', icon: ClipboardCheck, color: 'text-cyan-600', bg: 'bg-cyan-50', category: 'document' },
  { type: 'AMBULANCE_TRANSPORT_CERTIFICATE', label: 'Ambulance Transport Certificate', description: 'Ambulance dispatch record with patient monitoring, equipment, and handover details', icon: Ambulance, color: 'text-orange-600', bg: 'bg-orange-50', category: 'certificate' },
  { type: 'EMERGENCY_EVENT_CERTIFICATE', label: 'Emergency Event Certificate', description: 'Emergency incident record with triage and intervention details', icon: Siren, color: 'text-red-600', bg: 'bg-red-50', category: 'certificate' },
  { type: 'BLOOD_DONATION_CERTIFICATE', label: 'Blood Donation Certificate', description: 'Blood donation record with blood group, components, and eligibility details', icon: Droplets, color: 'text-rose-600', bg: 'bg-rose-50', category: 'certificate' },
  { type: 'INSURANCE_CLAIM_CERTIFICATE', label: 'Insurance Claim Pack', description: 'Complete claim documentation with policy, financial summary, and attached documents', icon: Shield, color: 'text-indigo-600', bg: 'bg-indigo-50', category: 'financial' },
  { type: 'HOSPITAL_ADMISSION_CERTIFICATE', label: 'Hospital Admission Certificate', description: 'Official admission record with ward, diagnosis, and attending physician details', icon: Building2, color: 'text-sky-600', bg: 'bg-sky-50', category: 'certificate' },
  { type: 'SURGERY_PROCEDURE_CERTIFICATE', label: 'Surgery / Procedure Certificate', description: 'Surgical or procedural record with pre-op/post-op details and outcomes', icon: Scissors, color: 'text-amber-600', bg: 'bg-amber-50', category: 'certificate' },
  { type: 'HEALTH_CHECKUP_CERTIFICATE', label: 'Health Checkup Certificate', description: 'Annual or periodic health checkup report with all parameters', icon: Activity, color: 'text-lime-600', bg: 'bg-lime-50', category: 'certificate' },
  { type: 'INSTITUTION_CLEARANCE_CERTIFICATE', label: 'Institution Medical Clearance', description: 'Medical clearance for institutional requirements (school, employment, travel)', icon: Award, color: 'text-fuchsia-600', bg: 'bg-fuchsia-50', category: 'certificate' },
  { type: 'INVOICE', label: 'Invoice / Receipt', description: 'Itemized billing with GST, payment details, and insurance coverage', icon: Receipt, color: 'text-gray-600', bg: 'bg-gray-100', category: 'financial' },
];

type Category = 'all' | 'certificate' | 'document' | 'financial';

export default function DocumentTemplates() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<Category>('all');
  const [nhcId, setNhcId] = useState('');
  /** The patient's own issued certificates, grouped by certificate type. */
  const [mine, setMine] = useState<Record<string, { id: string; title: string }[]>>({});
  const [loadingMine, setLoadingMine] = useState(true);

  useEffect(() => {
    let alive = true;
    patientApi.getProfile().then(p => { if (alive) setNhcId(p.nhcId); }).catch(() => {});
    patientApi.getCertificates()
      .then(list => {
        if (!alive) return;
        const grouped: Record<string, { id: string; title: string }[]> = {};
        for (const c of list ?? []) {
          (grouped[c.certificateType] ??= []).push({ id: c.id, title: c.title });
        }
        setMine(grouped);
      })
      .catch(() => { if (alive) setMine({}); })
      .finally(() => { if (alive) setLoadingMine(false); });
    return () => { alive = false; };
  }, []);

  const filtered = TEMPLATES.filter(t => filter === 'all' || t.category === filter);

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <FileText className="w-6 h-6 text-teal-600" /> Document Templates
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">Official NeoHealthCard document formats — generate, preview, and download</p>
        </div>
        {nhcId && <NHCIdBadge nhcId={nhcId} size="sm" />}
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1">
        {([
          { key: 'all', label: 'All Templates', count: TEMPLATES.length },
          { key: 'certificate', label: 'Certificates', count: TEMPLATES.filter(t => t.category === 'certificate').length },
          { key: 'document', label: 'Documents', count: TEMPLATES.filter(t => t.category === 'document').length },
          { key: 'financial', label: 'Financial', count: TEMPLATES.filter(t => t.category === 'financial').length },
        ] as { key: Category; label: string; count: number }[]).map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            className={`px-4 py-2 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${filter === f.key ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            {f.label} ({f.count})
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map(t => {
          const Icon = t.icon;
          return (
            <div key={t.type} className="card p-5 hover:shadow-md transition-shadow">
              <div className="flex items-start gap-3 mb-3">
                <div className={`w-11 h-11 rounded-xl ${t.bg} flex items-center justify-center shrink-0`}>
                  <Icon className={`w-5 h-5 ${t.color}`} />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900">{t.label}</p>
                  <p className="text-[10px] text-gray-400 uppercase tracking-wider">{t.category}</p>
                </div>
              </div>
              <p className="text-xs text-gray-500 leading-relaxed mb-3">{t.description}</p>

              {/* What the patient actually holds of this kind, from the server */}
              {(() => {
                const held = mine[t.type] ?? [];
                const info = ISSUED_BY[t.type];
                if (held.length > 0) {
                  return (
                    <>
                      <p className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-2.5 py-1.5 mb-3">
                        You have {held.length} issued {held.length === 1 ? 'copy' : 'copies'}
                      </p>
                      <button
                        onClick={() => navigate(`/patient/certificates/preview/${held[0].id}`)}
                        className="w-full px-3 py-2 bg-teal-600 text-white text-xs font-medium rounded-lg hover:bg-teal-700 flex items-center justify-center gap-1.5">
                        <Eye className="w-3.5 h-3.5" /> View mine
                      </button>
                    </>
                  );
                }
                return (
                  <>
                    <p className="text-[11px] text-gray-500 mb-3">
                      {loadingMine ? 'Checking your records…' : `Issued by ${info?.by ?? 'your provider'} — you do not hold one yet.`}
                    </p>
                    <button
                      onClick={() => navigate(info?.where ?? '/patient/certificates')}
                      className="w-full px-3 py-2 bg-white text-teal-700 border border-teal-200 text-xs font-medium rounded-lg hover:bg-teal-50">
                      {info?.whereLabel ?? 'My certificates'}
                    </button>
                  </>
                );
              })()}
            </div>
          );
        })}
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-[11px] text-amber-700">
        <p><b>How these are issued:</b> every document here is created and signed by the provider who treated you — a doctor, hospital, lab, blood bank or insurer. NeoHealthCard stores and verifies them; it never generates one on your behalf. Ask the provider to issue it, and it appears in your Certificates and Health Vault.</p>
      </div>
    </div>
  );
}
